/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import dayjs from "dayjs";

import { DigitalFarmerAccount } from "../../../azure-b2c/storage";
import { SalesAgreement, SalesAgreementProductLine } from "../../../sales-agreements/storage";
import { getAvailableQuantity } from "../../../sales-agreements/utils/getAvailableQuantity";

function levenshteinDistance(a: string, b: string): number {
  const matrix = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));

  for (let i = 0; i <= a.length; i++) {
    matrix[i][0] = i;
  }
  for (let j = 0; j <= b.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // Deletion
        matrix[i][j - 1] + 1, // Insertion
        matrix[i - 1][j - 1] + cost // Substitution
      );
    }
  }

  return matrix[a.length][b.length];
}

export interface AggregatedSalesAgreementProductLine extends SalesAgreementProductLine {
  max: number;
  maxLeft: number;
}

export function aggregateProductQuantities(
  products: SalesAgreementProductLine[]
): AggregatedSalesAgreementProductLine[] {
  const aggregationMap: Record<string, AggregatedSalesAgreementProductLine> = {};

  products.forEach((product) => {
    const variantNumber = product.productVariantNumber;

    if (!aggregationMap[variantNumber]) {
      aggregationMap[variantNumber] = {
        ...product,
        quantity: { ...product.quantity },
        max: product.quantity.committed,
        maxLeft:
          product.quantity.committed -
          product.quantity.released -
          product.quantity.delivered -
          product.quantity.invoiced,
      };
    } else {
      const existing = aggregationMap[variantNumber];

      // Sum up the quantities
      existing.quantity.committed += product.quantity.committed;
      existing.quantity.released += product.quantity.released;
      existing.quantity.delivered += product.quantity.delivered;
      existing.quantity.invoiced += product.quantity.invoiced;
      existing.quantity.remaining += product.quantity.remaining;

      // Recalculate max and maxLeft
      existing.max = existing.quantity.committed;
      existing.maxLeft =
        existing.max -
        existing.quantity.released -
        existing.quantity.delivered -
        existing.quantity.invoiced;
    }
  });

  return Object.values(aggregationMap);
}

function similarityPercentage(a: string, b: string): number {
  const distance = levenshteinDistance(a, b);
  const maxLength = Math.max(a.length, b.length);
  return ((maxLength - distance) / maxLength) * 100;
}

function isDrawdownRequestable(
  account: DigitalFarmerAccount | undefined,
  salesAgreement: SalesAgreement | undefined
): boolean {
  return Boolean(
    account?.hasCheckoutAccess &&
    account?.hasShareholderAccess &&
    salesAgreement?.supportsDelivery &&
    salesAgreement?.status === "Effective" &&
    getAvailableQuantity(salesAgreement) > 0 &&
    dayjs(salesAgreement.startDate).isBefore(dayjs().add(1, "month"))
  );
}

export { isDrawdownRequestable, similarityPercentage };
